import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { Input } from 'src/@/components/ui/input';
import { Switch } from 'src/@/components/ui/switch';
import { Badge } from 'src/@/components/ui/badge';
import { Checkbox } from 'src/@/components/ui/checkbox';
import Iconify from 'src/components/Iconify';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import widgetCmsService from 'src/services/widgetCmsService';
import NewsPostDrawer from './NewsPostDrawer';
import HelpArtigoDrawer from './HelpArtigoDrawer';
import ImageUploadField from './ImageUploadField';
import ChannelWebchat from '../canais/TypesChannels/ChannelWebchat';
import ChannelWebchatHours from '../canais/TypesChannels/ChannelWebchatHours';
import ChannelWebchatIdentify from '../canais/TypesChannels/ChannelWebchatIdentify';
import ChannelWebchatInstall from '../canais/TypesChannels/ChannelWebchatInstall';
import { WIDGET_CMS_DESIGN as D } from './WidgetCms.design';

const DEFAULT_GRADIENT = { from: '#7C3AED', to: '#EC4899' };
const DEFAULT_PRECHAT = { enabled: false, title: '', subtitle: '', fields: [] };
const DEFAULT_HOURS = { enabled: false, timezone: 'America/Sao_Paulo', outsideMessage: '', schedules: [] };

export default function ProjetoDetail({ clienteId, projeto: projetoInicial, onVoltar, onChanged }) {
    const { t } = useTranslation('settings');

    const TABS = [
        { id: 'config', label: t('widgetCms.projectDetail.tabs.config', 'Configurações'), icon: 'solar:settings-bold' },
        { id: 'identificacao', label: t('widgetCms.projectDetail.tabs.identification', 'Identificação'), icon: 'solar:user-id-bold' },
        { id: 'horario', label: t('widgetCms.projectDetail.tabs.hours', 'Horário'), icon: 'solar:clock-circle-bold' },
        { id: 'news', label: t('widgetCms.projectDetail.tabs.news', 'Novidades'), icon: 'solar:document-text-bold' },
        { id: 'help', label: t('widgetCms.projectDetail.tabs.help', 'Central de Ajuda'), icon: 'solar:book-bookmark-bold' },
        { id: 'canais', label: t('widgetCms.projectDetail.tabs.channels', 'Canais'), icon: 'fluent:channel-share-28-filled' },
        { id: 'instalacao', label: t('widgetCms.projectDetail.tabs.installation', 'Instalação'), icon: 'streamline-plump:web' }
    ];

    // Estado local do projeto: permite a transição criar → editar sem sair da página.
    const [projeto, setProjeto] = useState(projetoInicial);
    const isEdit = !!projeto?._id;
    const [tab, setTab] = useState('config');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [canaisWebchat, setCanaisWebchat] = useState([]);

    // Configurações
    const [nome, setNome] = useState('');
    const [saudacao, setSaudacao] = useState('');
    const [subtitulo, setSubtitulo] = useState('');
    const [gradientFrom, setGradientFrom] = useState(DEFAULT_GRADIENT.from);
    const [gradientTo, setGradientTo] = useState(DEFAULT_GRADIENT.to);
    const [logo, setLogo] = useState('');
    const [tabHome, setTabHome] = useState(true);
    const [tabMessages, setTabMessages] = useState(true);
    const [tabNews, setTabNews] = useState(true);
    const [tabHelp, setTabHelp] = useState(true);

    // Identificação (pré-chat) e horário de atendimento — geridos aqui na
    // Central do Widget (antes ficavam na config do canal webchat).
    const [preChat, setPreChat] = useState(DEFAULT_PRECHAT);
    const [businessHours, setBusinessHours] = useState(DEFAULT_HOURS);

    // Modo de identificação do visitante: 'form' (formulário de pré-chat) ou
    // 'identify' (o site host informa o usuário logado via LaisChat.identify).
    const [identMode, setIdentMode] = useState('form');
    const [identitySecret, setIdentitySecret] = useState('');

    // News
    const [news, setNews] = useState([]);
    const [newsDrawer, setNewsDrawer] = useState(null); // null | {post?}

    // Help
    const [categorias, setCategorias] = useState([]);
    const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
    const [artigos, setArtigos] = useState([]);
    const [artigoDrawer, setArtigoDrawer] = useState(null); // null | {artigo?}
    const [catForm, setCatForm] = useState(null); // null | {_id?, emoji, titulo, descricao, ordem}
    const [savingCat, setSavingCat] = useState(false);

    // Canais
    const [canaisSelecionados, setCanaisSelecionados] = useState([]);

    // Sincroniza com o projeto recebido (ao navegar para a página).
    useEffect(() => {
        setProjeto(projetoInicial);
    }, [projetoInicial]);

    // Lista de canais webchat do cliente (para a aba Canais).
    useEffect(() => {
        if (!clienteId) return;
        let ativo = true;
        actionListaCanaisPorCliente(clienteId)
            .then(res => {
                if (!ativo) return;
                const todos = res.data?.data || [];
                setCanaisWebchat(todos.filter(c => c.platform === 'webchat'));
            })
            .catch(() => {});
        return () => { ativo = false; };
    }, [clienteId]);

    useEffect(() => {
        if (projeto) {
            setNome(projeto.nome || '');
            setSaudacao(projeto.settings?.saudacao || '');
            setSubtitulo(projeto.settings?.subtitulo || '');
            setGradientFrom(projeto.settings?.gradient?.from || DEFAULT_GRADIENT.from);
            setGradientTo(projeto.settings?.gradient?.to || DEFAULT_GRADIENT.to);
            setLogo(projeto.settings?.logo || '');
            setTabHome(projeto.settings?.tabs?.home !== false);
            setTabMessages(projeto.settings?.tabs?.messages !== false);
            setTabNews(projeto.settings?.tabs?.news !== false);
            setTabHelp(projeto.settings?.tabs?.help !== false);
            setPreChat({ ...DEFAULT_PRECHAT, ...(projeto.settings?.preChat || {}) });
            setBusinessHours({ ...DEFAULT_HOURS, ...(projeto.settings?.businessHours || {}) });
            setIdentMode(projeto.settings?.identification?.mode || 'form');
            setIdentitySecret(projeto.settings?.identityVerification?.secret || '');
            setCanaisSelecionados((projeto.canais || []).map(c => c._id));
        }
    }, [projeto]);

    const carregarNews = useCallback(async () => {
        if (!projeto?._id) return;
        try {
            const res = await widgetCmsService.listarNews(projeto._id);
            setNews(res.data || []);
        } catch (e) {
            console.log('erro ao listar news', e);
        }
    }, [projeto?._id]);

    const carregarCategorias = useCallback(async () => {
        if (!projeto?._id) return;
        try {
            const res = await widgetCmsService.listarCategorias(projeto._id);
            setCategorias(res.data || []);
        } catch (e) {
            console.log('erro ao listar categorias', e);
        }
    }, [projeto?._id]);

    const carregarArtigos = useCallback(async (categoriaId) => {
        try {
            const res = await widgetCmsService.listarArtigos(categoriaId);
            setArtigos(res.data || []);
        } catch (e) {
            console.log('erro ao listar artigos', e);
        }
    }, []);

    useEffect(() => {
        carregarNews();
        carregarCategorias();
    }, [carregarNews, carregarCategorias]);

    useEffect(() => {
        if (categoriaSelecionada?._id) carregarArtigos(categoriaSelecionada._id);
        else setArtigos([]);
    }, [categoriaSelecionada?._id, carregarArtigos]);

    // Nunca deixa desligar a última aba ativa: sem nenhuma, o widget cairia no
    // fallback forçado de "Mensagens" (para não ficar sem nenhuma tela) e a
    // configuração salva não bateria mais com o que aparece de verdade — o
    // admin veria "Mensagens" desligada aqui, mas ligada no widget.
    const ultimaAbaAtiva = (chave) => {
        const estado = { home: tabHome, messages: tabMessages, news: tabNews, help: tabHelp };
        estado[chave] = false;
        return !Object.values(estado).some(Boolean);
    };
    const toggleTab = (chave, setter) => (checked) => {
        if (!checked && ultimaAbaAtiva(chave)) return;
        setter(checked);
    };

    const montarPayload = () => ({
        nome,
        settings: {
            saudacao,
            subtitulo,
            gradient: { from: gradientFrom, to: gradientTo },
            logo,
            tabs: { home: tabHome, messages: tabMessages, news: tabNews, help: tabHelp },
            preChat,
            businessHours,
            // Identificação: modo escolhido + secret (o backend gera/preserva a
            // secret quando mode === 'identify' | 'auto'; nunca é exposta ao widget).
            identification: { mode: identMode },
            ...(identitySecret ? { identityVerification: { secret: identitySecret } } : {})
        }
    });

    // Adapta o onChange dos componentes ChannelWebchat/Hours (que emitem
    // { target: { name: 'preChat.x' | 'businessHours.x', value } }) para o estado local.
    const handleWebchatChange = ({ target: { name, value } }) => {
        const [grupo, campo] = String(name).split('.');
        if (grupo === 'preChat') setPreChat(prev => ({ ...prev, [campo]: value }));
        else if (grupo === 'businessHours') setBusinessHours(prev => ({ ...prev, [campo]: value }));
    };
    const canalSintetico = { config: { preChat, businessHours } };

    const handleSave = async () => {
        if (!nome.trim()) {
            setError(t('widgetCms.projectDetail.nameRequiredError', 'O nome do projeto é obrigatório'));
            setTab('config');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (isEdit) {
                const res = await widgetCmsService.editarProjeto(projeto._id, montarPayload());
                await widgetCmsService.vincularCanais(projeto._id, canaisSelecionados);
                if (res?.data) setProjeto(res.data);
                onChanged?.();
                onVoltar();
            } else {
                // Cria e permanece na página, agora em modo edição, para adicionar conteúdo.
                const res = await widgetCmsService.criarProjeto(clienteId, montarPayload());
                onChanged?.();
                if (res?.data) setProjeto(res.data);
                setTab('config');
            }
        } catch (e) {
            setError(e?.message || t('widgetCms.projectDetail.saveError', 'Erro ao salvar o projeto'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteProjeto = async () => {
        if (!window.confirm(t('widgetCms.projectDetail.confirmDeleteProject', 'Excluir o projeto "{{nome}}"? O conteúdo deixará de aparecer no widget.', { nome: projeto.nome }))) return;
        try {
            await widgetCmsService.deletarProjeto(projeto._id);
            onChanged?.();
            onVoltar();
        } catch (e) {
            setError(e?.message || t('widgetCms.projectDetail.deleteError', 'Erro ao excluir o projeto'));
        }
    };

    const handleSalvarCategoria = async () => {
        if (!catForm?.titulo?.trim()) return;
        setSavingCat(true);
        try {
            const data = {
                emoji: catForm.emoji,
                titulo: catForm.titulo,
                descricao: catForm.descricao,
                ordem: Number(catForm.ordem) || 0
            };
            if (catForm._id) {
                await widgetCmsService.editarCategoria(catForm._id, data);
            } else {
                await widgetCmsService.criarCategoria(projeto._id, data);
            }
            setCatForm(null);
            carregarCategorias();
        } catch (e) {
            console.log('erro ao salvar categoria', e);
        } finally {
            setSavingCat(false);
        }
    };

    const handleDeleteCategoria = async (cat) => {
        if (!window.confirm(t('widgetCms.projectDetail.confirmDeleteCategory', 'Excluir a categoria "{{titulo}}" e todos os artigos dela?', { titulo: cat.titulo }))) return;
        await widgetCmsService.deletarCategoria(cat._id);
        if (categoriaSelecionada?._id === cat._id) setCategoriaSelecionada(null);
        carregarCategorias();
    };

    const handleDeleteNews = async (post) => {
        if (!window.confirm(t('widgetCms.projectDetail.confirmDeleteNews', 'Excluir o post "{{titulo}}"?', { titulo: post.titulo }))) return;
        await widgetCmsService.deletarNews(post._id);
        carregarNews();
    };

    const handleDeleteArtigo = async (artigo) => {
        if (!window.confirm(t('widgetCms.projectDetail.confirmDeleteArtigo', 'Excluir o artigo "{{titulo}}"?', { titulo: artigo.titulo }))) return;
        await widgetCmsService.deletarArtigo(artigo._id);
        carregarArtigos(categoriaSelecionada._id);
    };

    const toggleCanal = (canalId) => {
        setCanaisSelecionados(prev =>
            prev.includes(canalId) ? prev.filter(id => id !== canalId) : [...prev, canalId]
        );
    };

    const tabsDisponiveis = isEdit ? TABS : TABS.filter(tab => tab.id === 'config');

    return (
        <div className={D.pageDetail.container}>
            {/* Barra "Voltar" */}
            <div className={D.pageDetail.backBar}>
                <button className={D.pageDetail.backButton} onClick={onVoltar}>
                    <Iconify icon="eva:arrow-back-outline" width={18} />
                    {t('widgetCms.projectDetail.backButton', 'Voltar para projetos')}
                </button>
            </div>

            <div className="flex-1 flex min-h-0 overflow-hidden">
                {/* Sidebar vertical de abas */}
                <div className="w-52 flex-shrink-0 border-r border-gray-100 bg-gray-50/40 p-3 flex flex-col gap-1 overflow-y-auto">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 pt-1 pb-2">{t('widgetCms.projectDetail.sectionsLabel', 'Seções')}</p>
                    {tabsDisponiveis.map(tabItem => (
                        <button
                            key={tabItem.id}
                            onClick={() => setTab(tabItem.id)}
                            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-left transition-all ${
                                tab === tabItem.id
                                    ? 'bg-white text-[#7C3AED] font-semibold shadow-sm border border-violet-100'
                                    : 'text-gray-500 font-medium border border-transparent hover:bg-gray-100 hover:text-gray-700'
                            }`}
                        >
                            <Iconify icon={tabItem.icon} width={17} className="flex-shrink-0" />
                            <span>{tabItem.label}</span>
                        </button>
                    ))}
                </div>

                {/* Coluna de conteúdo */}
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {error && (
                    <div className={D.drawer.errorBanner}>
                        <Iconify icon="eva:alert-circle-outline" width={16} />
                        {error}
                    </div>
                )}

                {/* Content */}
                {tab === 'config' && (
                    <div className={D.drawer.content.container}>
                        <div className={D.drawer.content.leftColumn}>
                            <div className="flex flex-col gap-4 max-w-2xl">
                                {/* Informações */}
                                <div className="rounded-xl border border-gray-100 p-5">
                                    <h4 className="text-sm font-bold text-gray-900 mb-4">{t('widgetCms.projectDetail.config.infoSectionTitle', 'Informações')}</h4>
                                    <div>
                                        <label className={D.drawer.section.label}>{t('widgetCms.projectDetail.config.nameLabel', 'Nome do projeto')}</label>
                                        <Input
                                            value={nome}
                                            onChange={e => setNome(e.target.value)}
                                            placeholder={t('widgetCms.projectDetail.config.namePlaceholder', 'Ex.: Widget do site principal')}
                                            className={D.drawer.section.input}
                                        />
                                    </div>
                                </div>

                                {/* Textos da Home */}
                                <div className="rounded-xl border border-gray-100 p-5">
                                    <h4 className="text-sm font-bold text-gray-900 mb-4">{t('widgetCms.projectDetail.config.homeTextsSectionTitle', 'Textos da Home')}</h4>
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <label className={D.drawer.section.label}>{t('widgetCms.projectDetail.config.greetingLabel', 'Saudação')}</label>
                                            <Input
                                                value={saudacao}
                                                onChange={e => setSaudacao(e.target.value)}
                                                placeholder={t('widgetCms.projectDetail.config.greetingPlaceholder', 'Olá 👋')}
                                                className={D.drawer.section.input}
                                            />
                                        </div>
                                        <div>
                                            <label className={D.drawer.section.label}>{t('widgetCms.projectDetail.config.subtitleLabel', 'Subtítulo')}</label>
                                            <Input
                                                value={subtitulo}
                                                onChange={e => setSubtitulo(e.target.value)}
                                                placeholder={t('widgetCms.projectDetail.config.subtitlePlaceholder', 'Como podemos ajudar você hoje?')}
                                                className={D.drawer.section.input}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Aparência */}
                                <div className="rounded-xl border border-gray-100 p-5">
                                    <h4 className="text-sm font-bold text-gray-900 mb-4">{t('widgetCms.projectDetail.config.appearanceSectionTitle', 'Aparência')}</h4>
                                    <div className="flex flex-col gap-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className={D.drawer.section.label}>{t('widgetCms.projectDetail.config.gradientFromLabel', 'Gradiente — início')}</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="color"
                                                        value={gradientFrom}
                                                        onChange={e => setGradientFrom(e.target.value)}
                                                        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 flex-shrink-0"
                                                    />
                                                    <Input
                                                        value={gradientFrom}
                                                        onChange={e => setGradientFrom(e.target.value)}
                                                        className={D.drawer.section.input}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className={D.drawer.section.label}>{t('widgetCms.projectDetail.config.gradientToLabel', 'Gradiente — fim')}</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="color"
                                                        value={gradientTo}
                                                        onChange={e => setGradientTo(e.target.value)}
                                                        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 flex-shrink-0"
                                                    />
                                                    <Input
                                                        value={gradientTo}
                                                        onChange={e => setGradientTo(e.target.value)}
                                                        className={D.drawer.section.input}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className={D.drawer.section.label}>{t('widgetCms.projectDetail.config.gradientPreviewLabel', 'Prévia do gradiente')}</label>
                                            <div
                                                className="h-10 rounded-lg border border-gray-200"
                                                style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
                                            />
                                        </div>
                                        <ImageUploadField label={t('widgetCms.projectDetail.config.logoLabel', 'Logo (opcional)')} value={logo} onChange={setLogo} />
                                    </div>
                                </div>

                                {/* Abas do widget */}
                                <div className="rounded-xl border border-gray-100 p-5">
                                    <h4 className="text-sm font-bold text-gray-900 mb-4">{t('widgetCms.projectDetail.config.tabsSectionTitle', 'Abas do widget')}</h4>
                                    <div className="flex flex-col gap-2.5">
                                        <div className="flex items-center justify-between rounded-xl border border-gray-100 p-3.5 hover:border-gray-200 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                                                    <Iconify icon="solar:home-2-bold" width={18} className="text-violet-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">{t('widgetCms.projectDetail.config.homeTabTitle', 'Aba Home')}</p>
                                                    <p className="text-xs text-gray-500">{t('widgetCms.projectDetail.config.homeTabHint', 'Tela inicial com saudação e atalhos')}</p>
                                                </div>
                                            </div>
                                            <Switch checked={tabHome} onCheckedChange={toggleTab('home', setTabHome)} />
                                        </div>
                                        <div className="flex items-center justify-between rounded-xl border border-gray-100 p-3.5 hover:border-gray-200 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                                                    <Iconify icon="solar:chat-round-dots-bold" width={18} className="text-violet-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">{t('widgetCms.projectDetail.config.messagesTabTitle', 'Aba Mensagens')}</p>
                                                    <p className="text-xs text-gray-500">{t('widgetCms.projectDetail.config.messagesTabHint', 'Lista de conversas do visitante')}</p>
                                                </div>
                                            </div>
                                            <Switch checked={tabMessages} onCheckedChange={toggleTab('messages', setTabMessages)} />
                                        </div>
                                        <div className="flex items-center justify-between rounded-xl border border-gray-100 p-3.5 hover:border-gray-200 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                                                    <Iconify icon="solar:document-text-bold" width={18} className="text-violet-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">{t('widgetCms.projectDetail.config.newsTabTitle', 'Aba Novidades')}</p>
                                                    <p className="text-xs text-gray-500">{t('widgetCms.projectDetail.config.newsTabHint', 'Feed de changelog no widget')}</p>
                                                </div>
                                            </div>
                                            <Switch checked={tabNews} onCheckedChange={toggleTab('news', setTabNews)} />
                                        </div>
                                        <div className="flex items-center justify-between rounded-xl border border-gray-100 p-3.5 hover:border-gray-200 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                                                    <Iconify icon="solar:book-bookmark-bold" width={18} className="text-violet-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">{t('widgetCms.projectDetail.config.helpTabTitle', 'Aba Central de Ajuda')}</p>
                                                    <p className="text-xs text-gray-500">{t('widgetCms.projectDetail.config.helpTabHint', 'Categorias e artigos com busca')}</p>
                                                </div>
                                            </div>
                                            <Switch checked={tabHelp} onCheckedChange={toggleTab('help', setTabHelp)} />
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-2.5">
                                        {t('widgetCms.projectDetail.config.tabsMinHint', 'Pelo menos uma aba precisa ficar ativa.')}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className={D.drawer.content.rightColumn}>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{t('widgetCms.projectDetail.config.previewLabel', 'Preview da Home')}</p>
                            <div className={D.preview.widgetFrame}>
                                <div
                                    className={D.preview.gradientHeader}
                                    style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
                                >
                                    {logo && <img src={logo} alt="logo" className="w-8 h-8 rounded-lg mb-3 object-cover" />}
                                    <p className="text-lg font-bold">{saudacao || t('widgetCms.projectDetail.config.greetingPlaceholder', 'Olá 👋')}</p>
                                    <p className="text-xl font-bold mt-1 opacity-95">{subtitulo || t('widgetCms.projectDetail.config.subtitlePlaceholder', 'Como podemos ajudar você hoje?')}</p>
                                </div>
                                <div className={D.preview.body}>
                                    <div className="bg-white rounded-full border border-gray-200 px-4 py-2.5 flex items-center justify-between shadow-sm">
                                        <span className="text-xs text-gray-400">{t('widgetCms.projectDetail.config.previewAskPlaceholder', 'Pergunte qualquer coisa...')}</span>
                                        <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center">
                                            <Iconify icon="eva:arrow-upward-outline" width={12} className="text-white" />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <span className="bg-white border border-gray-200 rounded-full px-3 py-1.5 text-[11px] text-gray-600 shadow-sm">🐛 {t('widgetCms.projectDetail.config.previewReportChip', 'Reportar problema')}</span>
                                        <span className="bg-white border border-gray-200 rounded-full px-3 py-1.5 text-[11px] text-gray-600 shadow-sm">💡 {t('widgetCms.projectDetail.config.previewSuggestChip', 'Sugerir melhoria')}</span>
                                    </div>
                                    <div className="flex gap-2 justify-around bg-white rounded-xl border border-gray-100 py-2 mt-auto">
                                        {[
                                            tabHome && t('widgetCms.projectDetail.config.previewNavHome', 'Home'),
                                            tabMessages && t('widgetCms.projectDetail.config.previewNavMessages', 'Mensagens'),
                                            tabNews && t('widgetCms.projectDetail.tabs.news', 'Novidades'),
                                            tabHelp && t('widgetCms.projectDetail.config.previewNavHelp', 'Ajuda')
                                        ].filter(Boolean).map(item => (
                                            <span key={item} className="text-[10px] text-gray-400 font-medium">{item}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {tab === 'identificacao' && (
                    <div className={D.drawer.content.full}>
                        <div className="max-w-3xl">
                            <div className="mb-4">
                                <h3 className="text-base font-bold text-gray-900">{t('widgetCms.projectDetail.identification.title', 'Identificação do visitante')}</h3>
                                <p className="text-xs text-gray-500">
                                    {t('widgetCms.projectDetail.identification.subtitle', 'Escolha como o widget descobre quem é o visitante ao iniciar uma conversa. Aplicado aos canais webchat vinculados a este projeto.')}
                                </p>
                            </div>

                            {/* Seletor de modo */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                                <button
                                    type="button"
                                    onClick={() => setIdentMode('form')}
                                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                                        identMode === 'form'
                                            ? 'border-[#7C3AED] bg-violet-50/50 shadow-sm'
                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Iconify icon="solar:clipboard-text-bold" width={18} className={identMode === 'form' ? 'text-[#7C3AED]' : 'text-gray-400'} />
                                        <span className="text-sm font-bold text-gray-900">{t('widgetCms.projectDetail.identification.formModeTitle', 'Formulário de pré-chat')}</span>
                                        {identMode === 'form' && (
                                            <Iconify icon="eva:checkmark-circle-2-fill" width={16} className="text-[#7C3AED] ml-auto" />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        {t('widgetCms.projectDetail.identification.formModeDescription', 'O visitante preenche os dados antes de conversar. Funciona em qualquer site, sem integração.')}
                                    </p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setIdentMode('identify')}
                                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                                        identMode === 'identify'
                                            ? 'border-[#7C3AED] bg-violet-50/50 shadow-sm'
                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Iconify icon="solar:shield-check-bold" width={18} className={identMode === 'identify' ? 'text-[#7C3AED]' : 'text-gray-400'} />
                                        <span className="text-sm font-bold text-gray-900">{t('widgetCms.projectDetail.identification.autoModeTitle', 'Automático (usuário logado)')}</span>
                                        {identMode === 'identify' && (
                                            <Iconify icon="eva:checkmark-circle-2-fill" width={16} className="text-[#7C3AED] ml-auto" />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        <Trans t={t} i18nKey="widgetCms.projectDetail.identification.autoModeDescription" components={{ code: <code className="font-mono" /> }}>
                                            Seu site informa o usuário logado via <code>identify()</code>. Sem fricção, com selo de verificado.
                                        </Trans>
                                    </p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setIdentMode('auto')}
                                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                                        identMode === 'auto'
                                            ? 'border-[#7C3AED] bg-violet-50/50 shadow-sm'
                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Iconify icon="solar:users-group-rounded-bold" width={18} className={identMode === 'auto' ? 'text-[#7C3AED]' : 'text-gray-400'} />
                                        <span className="text-sm font-bold text-gray-900">{t('widgetCms.projectDetail.identification.hybridModeTitle', 'Híbrido (recomendado para SaaS)')}</span>
                                        {identMode === 'auto' && (
                                            <Iconify icon="eva:checkmark-circle-2-fill" width={16} className="text-[#7C3AED] ml-auto" />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        <Trans t={t} i18nKey="widgetCms.projectDetail.identification.hybridModeDescription" components={{ code: <code className="font-mono" /> }}>
                                            Formulário para visitante anônimo (landing) e <code>identify()</code> automático para quem já está logado (painel) — no mesmo canal.
                                        </Trans>
                                    </p>
                                </button>
                            </div>

                            {identMode === 'form' && (
                                <ChannelWebchat canal={canalSintetico} onChange={handleWebchatChange} />
                            )}
                            {identMode === 'identify' && (
                                <ChannelWebchatIdentify secret={identitySecret} />
                            )}
                            {identMode === 'auto' && (
                                <div className="flex flex-col gap-6">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                            {t('widgetCms.projectDetail.identification.hybridFormSectionTitle', 'Formulário — visitante anônimo (ex.: landing page)')}
                                        </p>
                                        <ChannelWebchat canal={canalSintetico} onChange={handleWebchatChange} />
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                            {t('widgetCms.projectDetail.identification.hybridIdentifySectionTitle', 'Automático — visitante logado (ex.: painel do seu SaaS)')}
                                        </p>
                                        <ChannelWebchatIdentify secret={identitySecret} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === 'horario' && (
                    <div className={D.drawer.content.full}>
                        <div className="max-w-3xl">
                            <div className="mb-4">
                                <h3 className="text-base font-bold text-gray-900">{t('widgetCms.projectDetail.hours.title', 'Horário de atendimento')}</h3>
                                <p className="text-xs text-gray-500">
                                    {t('widgetCms.projectDetail.hours.subtitle', 'Exibe um aviso de "fora do horário" no chat fora dos períodos configurados. Aplicado aos canais webchat vinculados a este projeto.')}
                                </p>
                            </div>
                            <ChannelWebchatHours canal={canalSintetico} onChange={handleWebchatChange} />
                        </div>
                    </div>
                )}

                {tab === 'news' && (
                    <div className={D.drawer.content.full}>
                        <div className="max-w-4xl">
                            <div className="flex items-center justify-between mb-5">
                                <div>
                                    <h3 className="text-base font-bold text-gray-900">{t('widgetCms.projectDetail.news.title', 'Posts de novidades')}</h3>
                                    <p className="text-xs text-gray-500">{t('widgetCms.projectDetail.news.subtitle', 'Aparecem na aba Novidades e no destaque da Home')}</p>
                                </div>
                                <button className={D.page.actionButton} onClick={() => setNewsDrawer({})}>
                                    <Iconify icon="eva:plus-outline" width={16} />
                                    {t('widgetCms.projectDetail.news.newPostButton', 'Novo post')}
                                </button>
                            </div>
                            {news.length === 0 ? (
                                <div className={D.emptyState.container}>
                                    <div className={D.emptyState.iconBox}>
                                        <Iconify icon="solar:document-text-bold" width={28} className="text-violet-300" />
                                    </div>
                                    <p className={D.emptyState.title}>{t('widgetCms.projectDetail.news.emptyTitle', 'Nenhum post ainda')}</p>
                                    <p className={D.emptyState.subtitle}>{t('widgetCms.projectDetail.news.emptySubtitle', 'Anuncie novidades do seu produto para os visitantes')}</p>
                                    <span className={D.emptyState.link} onClick={() => setNewsDrawer({})}>{t('widgetCms.projectDetail.news.emptyLink', 'Criar o primeiro post')}</span>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {news.map(post => (
                                        <div key={post._id} className={D.listItem.container} onClick={() => setNewsDrawer({ post })}>
                                            {post.imagem ? (
                                                <img src={post.imagem} alt="" className="w-14 h-10 rounded-lg object-cover flex-shrink-0" />
                                            ) : (
                                                <div className="w-14 h-10 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                                                    <Iconify icon="eva:image-outline" width={16} className="text-violet-300" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className={D.listItem.title}>{post.titulo}</p>
                                                <p className={D.listItem.subtitle}>{post.descricao}</p>
                                            </div>
                                            {post.publicado ? (
                                                <Badge variant="outline" className={D.badge.publicado}>
                                                    <span className={D.badge.dotVerde} />
                                                    {t('status.published', 'Publicado', { ns: 'common' })}
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className={D.badge.rascunho}>
                                                    <span className={D.badge.dotAmarelo} />
                                                    {t('status.draft', 'Rascunho', { ns: 'common' })}
                                                </Badge>
                                            )}
                                            <button
                                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                onClick={e => { e.stopPropagation(); handleDeleteNews(post); }}
                                            >
                                                <Iconify icon="eva:trash-2-outline" width={16} />
                                            </button>
                                            <Iconify icon="eva:chevron-right-outline" width={18} className="text-gray-300 group-hover:text-[#7C3AED]" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === 'help' && (
                    <div className={D.drawer.content.container}>
                        {/* Categorias */}
                        <div className="w-[40%] p-6 overflow-y-auto max-h-full border-r border-gray-100 bg-white">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-bold text-gray-900">{t('widgetCms.projectDetail.help.categoriesTitle', 'Categorias')}</h3>
                                <button
                                    className="text-xs text-[#7C3AED] font-medium hover:underline flex items-center gap-1"
                                    onClick={() => setCatForm({ emoji: '📘', titulo: '', descricao: '', ordem: categorias.length })}
                                >
                                    <Iconify icon="eva:plus-outline" width={14} />
                                    {t('widgetCms.projectDetail.help.newCategoryButton', 'Nova categoria')}
                                </button>
                            </div>
                            {catForm && (
                                <div className="bg-violet-50/50 rounded-xl border border-violet-100 p-4 mb-4 flex flex-col gap-3">
                                    <div className="flex gap-2">
                                        <Input
                                            value={catForm.emoji || ''}
                                            onChange={e => setCatForm({ ...catForm, emoji: e.target.value })}
                                            placeholder="📘"
                                            className={`${D.drawer.section.input} w-16 text-center`}
                                        />
                                        <Input
                                            value={catForm.titulo}
                                            onChange={e => setCatForm({ ...catForm, titulo: e.target.value })}
                                            placeholder={t('widgetCms.projectDetail.help.categoryTitlePlaceholder', 'Título da categoria')}
                                            className={D.drawer.section.input}
                                        />
                                    </div>
                                    <Input
                                        value={catForm.descricao || ''}
                                        onChange={e => setCatForm({ ...catForm, descricao: e.target.value })}
                                        placeholder={t('widgetCms.projectDetail.help.categoryDescriptionPlaceholder', 'Descrição curta')}
                                        className={D.drawer.section.input}
                                    />
                                    <div className="flex items-center justify-end gap-2">
                                        <button className="text-xs text-gray-500 hover:text-gray-700 px-2" onClick={() => setCatForm(null)}>
                                            {t('buttons.cancel', 'Cancelar', { ns: 'common' })}
                                        </button>
                                        <ButtonLoading loading={savingCat} onClick={handleSalvarCategoria} className={D.page.actionButton}>
                                            {catForm._id ? t('buttons.save', 'Salvar', { ns: 'common' }) : t('buttons.create', 'Criar', { ns: 'common' })}
                                        </ButtonLoading>
                                    </div>
                                </div>
                            )}
                            <div className="flex flex-col gap-2">
                                {categorias.map(cat => (
                                    <div
                                        key={cat._id}
                                        className={`${D.listItem.container} ${categoriaSelecionada?._id === cat._id ? 'border-[#7C3AED]/60 bg-violet-50/50' : ''}`}
                                        onClick={() => setCategoriaSelecionada(cat)}
                                    >
                                        <span className="text-lg">{cat.emoji || '📘'}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className={D.listItem.title}>{cat.titulo}</p>
                                            <p className={D.listItem.subtitle}>{t('widgetCms.projectDetail.help.articleCount', '{{count}} artigo(s)', { count: cat.artigosCount || 0 })}</p>
                                        </div>
                                        <button
                                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-[#7C3AED] hover:bg-violet-50 transition-colors"
                                            onClick={e => { e.stopPropagation(); setCatForm({ ...cat }); }}
                                        >
                                            <Iconify icon="eva:edit-2-outline" width={14} />
                                        </button>
                                        <button
                                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                            onClick={e => { e.stopPropagation(); handleDeleteCategoria(cat); }}
                                        >
                                            <Iconify icon="eva:trash-2-outline" width={14} />
                                        </button>
                                    </div>
                                ))}
                                {categorias.length === 0 && !catForm && (
                                    <p className="text-xs text-gray-400 text-center py-6">{t('widgetCms.projectDetail.help.emptyCategories', 'Nenhuma categoria. Crie a primeira para organizar os artigos.')}</p>
                                )}
                            </div>
                        </div>

                        {/* Artigos da categoria */}
                        <div className="flex-1 p-6 overflow-y-auto max-h-full bg-gray-50/50">
                            {categoriaSelecionada ? (
                                <>
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900">
                                                {categoriaSelecionada.emoji} {categoriaSelecionada.titulo}
                                            </h3>
                                            <p className="text-xs text-gray-500">{categoriaSelecionada.descricao}</p>
                                        </div>
                                        <button className={D.page.actionButton} onClick={() => setArtigoDrawer({})}>
                                            <Iconify icon="eva:plus-outline" width={16} />
                                            {t('widgetCms.projectDetail.help.newArticleButton', 'Novo artigo')}
                                        </button>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {artigos.map(artigo => (
                                            <div key={artigo._id} className={`${D.listItem.container} bg-white`} onClick={() => setArtigoDrawer({ artigo })}>
                                                <div className="flex-1 min-w-0">
                                                    <p className={D.listItem.title}>{artigo.titulo}</p>
                                                    <p className={D.listItem.subtitle}>{artigo.descricao}</p>
                                                </div>
                                                {!artigo.publicado && (
                                                    <Badge variant="outline" className={D.badge.rascunho}>
                                                        <span className={D.badge.dotAmarelo} />
                                                        {t('status.draft', 'Rascunho', { ns: 'common' })}
                                                    </Badge>
                                                )}
                                                <button
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                    onClick={e => { e.stopPropagation(); handleDeleteArtigo(artigo); }}
                                                >
                                                    <Iconify icon="eva:trash-2-outline" width={16} />
                                                </button>
                                                <Iconify icon="eva:chevron-right-outline" width={18} className="text-gray-300 group-hover:text-[#7C3AED]" />
                                            </div>
                                        ))}
                                        {artigos.length === 0 && (
                                            <p className="text-xs text-gray-400 text-center py-6">{t('widgetCms.projectDetail.help.emptyArticles', 'Nenhum artigo nesta categoria ainda.')}</p>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className={D.emptyState.container}>
                                    <div className={D.emptyState.iconBox}>
                                        <Iconify icon="solar:book-bookmark-bold" width={28} className="text-violet-300" />
                                    </div>
                                    <p className={D.emptyState.title}>{t('widgetCms.projectDetail.help.selectCategoryTitle', 'Selecione uma categoria')}</p>
                                    <p className={D.emptyState.subtitle}>{t('widgetCms.projectDetail.help.selectCategorySubtitle', 'Os artigos da categoria aparecem aqui')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === 'canais' && (
                    <div className={D.drawer.content.full}>
                        <div className="max-w-2xl">
                            <h3 className="text-base font-bold text-gray-900 mb-1">{t('widgetCms.projectDetail.channels.title', 'Canais vinculados')}</h3>
                            <p className="text-xs text-gray-500 mb-5">
                                {t('widgetCms.projectDetail.channels.subtitle', 'O conteúdo deste projeto aparece nos widgets dos canais webchat selecionados. Cada canal só pode ter um projeto.')}
                            </p>
                            {canaisWebchat.length === 0 ? (
                                <div className={D.emptyState.container}>
                                    <div className={D.emptyState.iconBox}>
                                        <Iconify icon="fluent:channel-share-28-filled" width={28} className="text-violet-300" />
                                    </div>
                                    <p className={D.emptyState.title}>{t('widgetCms.projectDetail.channels.emptyTitle', 'Nenhum canal webchat')}</p>
                                    <p className={D.emptyState.subtitle}>{t('widgetCms.projectDetail.channels.emptySubtitle', 'Crie um canal do tipo webchat em Configurações → Canais')}</p>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {canaisWebchat.map(canal => {
                                        const vinculadoOutro = canal.config?.widget?.projetoId &&
                                            canal.config.widget.projetoId !== projeto?._id;
                                        return (
                                            <label
                                                key={canal._id}
                                                className={`${D.listItem.container} ${canaisSelecionados.includes(canal._id) ? 'border-[#7C3AED]/60 bg-violet-50/50' : ''}`}
                                            >
                                                <Checkbox
                                                    checked={canaisSelecionados.includes(canal._id)}
                                                    onCheckedChange={() => toggleCanal(canal._id)}
                                                />
                                                <div className="flex-1">
                                                    <p className={D.listItem.title}>{canal.nome}</p>
                                                    {vinculadoOutro && (
                                                        <p className="text-[11px] text-amber-600">
                                                            {t('widgetCms.projectDetail.channels.linkedToOtherWarning', 'Vinculado a outro projeto — marcar aqui transfere o vínculo')}
                                                        </p>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                            <p className="text-[11px] text-gray-400 mt-4">
                                {t('widgetCms.projectDetail.channels.saveHint', 'As alterações de vínculo são aplicadas ao clicar em Salvar. Pode levar até 30s para refletir no widget.')}
                            </p>
                        </div>
                    </div>
                )}

                {tab === 'instalacao' && (
                    <div className={D.drawer.content.full}>
                        <div className="max-w-2xl">
                            <ChannelWebchatInstall
                                canais={canaisWebchat.filter((canal) => canaisSelecionados.includes(canal._id))}
                            />
                        </div>
                    </div>
                )}
                </div>
            </div>

            {/* Footer */}
            <div className={D.pageDetail.footer}>
                <div className={D.drawer.footer.left}>
                    {isEdit && (
                        <button
                            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            onClick={handleDeleteProjeto}
                        >
                            <Iconify icon="eva:trash-2-outline" width={16} />
                            {t('widgetCms.projectDetail.deleteProjectButton', 'Excluir projeto')}
                        </button>
                    )}
                </div>
                <div className={D.drawer.footer.right}>
                    <button
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                        onClick={onVoltar}
                    >
                        {t('buttons.cancel', 'Cancelar', { ns: 'common' })}
                    </button>
                    <ButtonLoading loading={saving} onClick={handleSave} className={D.page.actionButton}>
                        <Iconify icon="eva:save-outline" width={16} />
                        {isEdit ? t('widgetCms.projectDetail.saveProjectButton', 'Salvar projeto') : t('widgetCms.projectDetail.createProjectButton', 'Criar projeto')}
                    </ButtonLoading>
                </div>
            </div>

            {/* Drawers aninhados */}
                {newsDrawer && (
                    <NewsPostDrawer
                        projetoId={projeto._id}
                        post={newsDrawer.post}
                        onClose={() => setNewsDrawer(null)}
                        onSaved={() => { setNewsDrawer(null); carregarNews(); }}
                    />
                )}
                {artigoDrawer && categoriaSelecionada && (
                    <HelpArtigoDrawer
                        categoria={categoriaSelecionada}
                        artigo={artigoDrawer.artigo}
                        onClose={() => setArtigoDrawer(null)}
                        onSaved={() => {
                            setArtigoDrawer(null);
                            carregarArtigos(categoriaSelecionada._id);
                            carregarCategorias();
                        }}
                    />
                )}
        </div>
    );
}

ProjetoDetail.propTypes = {
    clienteId: PropTypes.string,
    projeto: PropTypes.object,
    onVoltar: PropTypes.func.isRequired,
    onChanged: PropTypes.func
};
